'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuthStore } from '@/stores/authStore';
import { GuideOrbLoading } from '@/components/ui/GuideOrb';
import { useTranslations } from 'next-intl';
import { isPublicRoutePath } from '@/lib/routing/public-routes';
import { buildLoginRedirectPath, handleAuthExpiredRedirect } from '@/lib/auth/session';
import { useFeatureFlagsStore } from '@/lib/stores/feature-flags-store';

interface AuthGuardProps {
  children: React.ReactNode;
  initialPathname?: string;
}

// 加载超时时间（毫秒）
const HYDRATION_TIMEOUT = 3000;  // 3秒（缩短超时）
const SESSION_CHECK_TIMEOUT = 10000;  // 10秒

export function AuthGuard({ children, initialPathname }: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated, accessToken, user, checkSession } = useAuthStore();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [sessionRetryNonce, setSessionRetryNonce] = useState(0);
  const sessionVerificationKeyRef = useRef<string | null>(null);
  const sessionCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionRecoveryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionRecoveryAttemptRef = useRef(0);
  const t = useTranslations('auth');
  const effectivePathname = pathname || initialPathname;
  const isPublicRoute = isPublicRoutePath(effectivePathname);
  const isAdminRoute = effectivePathname === '/admin' || Boolean(effectivePathname?.startsWith('/admin/'));
  const hasConfirmedSession = isAuthenticated && Boolean(accessToken);

  // Account capabilities belong to the session, not an optional sidebar tool.
  // Direct Explore/project entry must load them without visiting Login first.
  useEffect(() => {
    if (!isHydrated) return;
    const flags = useFeatureFlagsStore.getState();
    flags.clearFlags();
    if (hasConfirmedSession && user?.id) void flags.initFlags();
  }, [isHydrated, hasConfirmedSession, user?.id]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'octo-auth') return;
      // The versioned auth-store channel owns recovery here. Reloading this
      // public authoring surface would discard input before login completes.
      if (window.location.pathname === '/kagemane') return;

      let nextUserId: string | null = null;
      let nextAuthenticated = false;

      if (event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue);
          nextUserId = parsed?.state?.user?.id ?? null;
          nextAuthenticated = !!parsed?.state?.isAuthenticated;
        } catch {
          nextUserId = null;
          nextAuthenticated = false;
        }
      }

      const currentState = useAuthStore.getState();
      const currentUserId = currentState.user?.id ?? null;
      if (nextUserId === currentUserId && nextAuthenticated === currentState.isAuthenticated) {
        return;
      }

      // 当前标签页无法感知其他标签页中的账号切换，直接重载以重新校验受保护路由。
      window.location.reload();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // 等待 Zustand persist hydration 完成
  useEffect(() => {
    // 立即检查是否已经 hydrated
    const alreadyHydrated = useAuthStore.persist?.hasHydrated?.() ?? true;
    
    if (alreadyHydrated) {
      setIsHydrated(true);
      return;
    }
    
    // 订阅 hydration 完成事件
    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = useAuthStore.persist.onFinishHydration(() => {
        setIsHydrated(true);
      });
    } catch (e) {
      console.warn('[AuthGuard] Failed to subscribe to hydration:', e);
      setIsHydrated(true);
    }
    
    // 兜底超时：3秒后强制继续
    const timeout = setTimeout(() => {
      setIsHydrated(true);
    }, HYDRATION_TIMEOUT);

    return () => {
      unsubscribe?.();
      clearTimeout(timeout);
    };
  }, []);


  // Hydration 完成后检查 session
  useEffect(() => {
    if (!isHydrated) return;

    const verificationKey = isAdminRoute
      ? `admin:${effectivePathname || '/admin'}:${sessionRetryNonce}`
      : `${isPublicRoute ? 'public' : 'protected'}:${effectivePathname || '/'}:${sessionRetryNonce}`;
    if (sessionVerificationKeyRef.current === verificationKey) return;
    sessionVerificationKeyRef.current = verificationKey;
    let active = true;
    setIsChecking(true);
    setErrorKey(null);

    // 公开页不阻塞渲染，只在后台尝试恢复会话。
    if (isPublicRoute) {
      checkSession()
        .catch(() => {
          // Public routes stay accessible even if session recovery fails.
        })
        .finally(() => {
          if (active) setIsChecking(false);
        });

      return () => { active = false; };
    }
    
    // 设置 session 检查超时
    sessionCheckTimeoutRef.current = setTimeout(() => {
      setErrorKey('authTimeout');
      setIsChecking(false);
    }, SESSION_CHECK_TIMEOUT);
    
    // 始终验证 session（即使本地有缓存状态）
    checkSession(isAdminRoute || sessionRetryNonce > 0)
      .then(() => {
        if (!active) return;
        const current = useAuthStore.getState();
        if (current.isAuthenticated && !current.accessToken) {
          setErrorKey('authRecovering');
          if (sessionRecoveryAttemptRef.current < 3) {
            const delay = 1200 * Math.pow(2, sessionRecoveryAttemptRef.current);
            sessionRecoveryAttemptRef.current += 1;
            sessionRecoveryTimerRef.current = setTimeout(() => {
              setSessionRetryNonce((value) => value + 1);
            }, delay);
          }
        } else if (current.accessToken) {
          sessionRecoveryAttemptRef.current = 0;
          setErrorKey(null);
        }
      })
      .catch(() => {
        if (active) setErrorKey('authFailed');
      })
      .finally(() => {
        if (!active) return;
        if (sessionCheckTimeoutRef.current) {
          clearTimeout(sessionCheckTimeoutRef.current);
          sessionCheckTimeoutRef.current = null;
        }
        setIsChecking(false);
      });
    
    return () => {
      active = false;
      if (sessionCheckTimeoutRef.current) {
        clearTimeout(sessionCheckTimeoutRef.current);
      }
      if (sessionRecoveryTimerRef.current) {
        clearTimeout(sessionRecoveryTimerRef.current);
        sessionRecoveryTimerRef.current = null;
      }
    };
  }, [isHydrated, checkSession, isPublicRoute, isAdminRoute, effectivePathname, sessionRetryNonce]);

  // 路由保护
  useEffect(() => {
    if (!isHydrated || isChecking) return;

    // 未登录 → 非公开路由跳登录
    if (!hasConfirmedSession && !isAuthenticated && !isPublicRoute) {
      // 失效的受保护页不应留在历史栈里，否则“返回”会再次触发登录跳转。
      router.replace(buildLoginRedirectPath());
      return;
    }

    if (isAdminRoute && !user?.isAdmin) {
      router.replace('/explore');
    }
  }, [hasConfirmedSession, isAuthenticated, user?.isAdmin, pathname, router, isHydrated, isChecking, isPublicRoute, isAdminRoute]);

  // 公开路由直接渲染，不等待 hydration 或 session 校验。
  if (isPublicRoute) {
    return <>{children}</>;
  }
  
  // 等待 hydration 和 session 检查完成
  if (errorKey && !hasConfirmedSession && !isPublicRoute) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md px-6">
          <div className="w-16 h-16 mx-auto bg-accent-soft rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-accent-core" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('loadingError')}</h2>
            <p className="text-gray-500 text-sm">{t(errorKey)}</p>
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                if (sessionRecoveryTimerRef.current) {
                  clearTimeout(sessionRecoveryTimerRef.current);
                  sessionRecoveryTimerRef.current = null;
                }
                sessionRecoveryAttemptRef.current = 0;
                sessionVerificationKeyRef.current = null;
                setErrorKey(null);
                setSessionRetryNonce((value) => value + 1);
              }}
              className="px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
            >
              {t('retrySession')}
            </button>
            <button
              onClick={() => {
                void handleAuthExpiredRedirect().catch(() => {});
              }}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              {t('resetLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isHydrated || (isChecking && (!hasConfirmedSession || isAdminRoute))) {
    return <GuideOrbLoading label={t('verifyingSession')} />;
  }

  // 未登录且不在公开页面，显示空白（等待跳转）
  if (!hasConfirmedSession && !isPublicRoute) {
    return null;
  }

  if (isAdminRoute && !user?.isAdmin) {
    return null;
  }

  return <>{children}</>;
}

export default AuthGuard;
